import { color } from "@rneui/base";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Platform,
  StyleProp,
  StyleSheet,
  TextStyle,
  TouchableNativeFeedback,
} from "react-native";
import {
  Button as RNEButton,
  ButtonProps,
  useTheme,
} from "react-native-elements";
import { getPrimaryButtonTextColor } from "./Miscellaneous";

export function Button(
  props: Omit<ButtonProps, "onPress"> & {
    onPress: () => void;
    enableAutoRepress?: boolean;
    autoRepressConfig?: {
      minTimeMs?: number;
      startingThreshold?: number;
      startingRepressTime?: number;
      dropoffMultiplier?: number;
    };
  },
) {
  const theme = useTheme();
  const {
    disabled,
    disabledTitleStyle,
    titleStyle,
    containerStyle,
    autoRepressConfig,
    enableAutoRepress,
    ...rest
  } = props;
  const disabledTitleStyleComputed = useMemo(
    () => ({
      color: getPrimaryButtonTextColor(theme.theme, true),
    }),
    [theme],
  );
  const pressed = useRef(false);
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  const currentRepressTimeout = useRef(300);
  const currentRepressHandler = useRef<NodeJS.Timeout | null>(null);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (flash) {
      const timeoutHandler = setTimeout(
        () => setFlash(false),
        (autoRepressConfig?.minTimeMs ?? 100) / 3,
      );
      return () => clearTimeout(timeoutHandler);
    }
  }, [flash]);
  const rePress = useCallback(() => {
    if (pressed.current) {
      if (!disabledRef.current) {
        props.onPress && props.onPress();
      }
      setFlash(true);
      currentRepressTimeout.current = Math.max(
        autoRepressConfig?.minTimeMs ?? 100,
        currentRepressTimeout.current *
          (autoRepressConfig?.dropoffMultiplier ?? 0.94),
      );
      currentRepressHandler.current = setTimeout(
        rePress,
        currentRepressTimeout.current,
      );
    }
  }, [props.onPress]);
  const onPressInCallback = useCallback(() => {
    pressed.current = true;
    currentRepressHandler.current = setTimeout(() => {
      if (pressed.current && !disabledRef.current) {
        currentRepressTimeout.current =
          autoRepressConfig?.startingRepressTime ?? 300;
        rePress();
      }
    }, autoRepressConfig?.startingThreshold ?? 600);
  }, [rePress]);
  const onPressOutCallback = useCallback(() => {
    pressed.current = false;
    if (currentRepressHandler.current != null) {
      clearTimeout(currentRepressHandler.current);
      currentRepressHandler.current = null;
    }
  }, [rePress]);
  useEffect(() => {
    return () => {
      onPressOutCallback();
    };
  }, [onPressOutCallback]);
  const titleStyleFlat: StyleProp<TextStyle> = useMemo(
    () =>
      StyleSheet.flatten([
        {
          color: getPrimaryButtonTextColor(theme.theme, false),
        },
        titleStyle,
      ]),
    [titleStyle, theme],
  );
  const background = useMemo(
    () =>
      Platform.OS === "android" && Platform.Version >= 21 && !flash
        ? TouchableNativeFeedback.Ripple(
            color(titleStyleFlat?.color?.toString())
              .alpha(theme.theme.mode === "dark" ? 0.1 : 0.2)
              .rgb()
              .string(),
            true,
          )
        : undefined,
    [titleStyleFlat, flash, theme],
  );
  const containerStyleFlat = useMemo(
    () =>
      StyleSheet.flatten([
        containerStyle,
        flash && Platform.OS != "android" && { opacity: 0.9 },
      ]),
    [containerStyle, theme, flash],
  );
  const disabledTitleStyleArray = useMemo(
    () => [disabledTitleStyleComputed, disabledTitleStyle],
    [disabledTitleStyleComputed, disabledTitleStyle],
  );
  return useMemo(
    () => (
      <RNEButton
        disabledTitleStyle={disabledTitleStyleArray}
        onPressIn={enableAutoRepress ? onPressInCallback : undefined}
        onPressOut={enableAutoRepress ? onPressOutCallback : undefined}
        disabled={disabled}
        titleStyle={titleStyleFlat}
        background={background}
        containerStyle={containerStyleFlat}
        {...rest}
      />
    ),
    [
      disabledTitleStyleArray,
      enableAutoRepress,
      onPressInCallback,
      onPressOutCallback,
      background,
      rest.onPress,
      rest.title,
      rest.icon,
      containerStyleFlat,
      titleStyleFlat,
      disabled,
      flash,
    ],
  );
}
